import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../components/layout/Topbar.jsx";
import CompanySelector from "../components/ui/CompanySelector.jsx";
import { AssetStatusBadge, Pagination, Modal } from "../components/ui/index.jsx";
import { adminGetAssets, adminCreateAsset } from "../services/assetService.js";
import { getCompanies } from "../services/companyService.js";

// ─────────────────────────────────────────────
//  CONSTANTES
// ─────────────────────────────────────────────

const ICONS = {
  SERVER:"bi-hdd-rack", COMPUTER:"bi-pc-display-horizontal",
  WORKSTATION:"bi-pc-display-horizontal", PRINTER:"bi-printer",
  ROUTER:"bi-router", SWITCH:"bi-diagram-3", FIREWALL:"bi-shield-lock",
  ACCESS_POINT:"bi-wifi", NETWORK:"bi-router", OTHER:"bi-cpu",
};
const ICON_COLORS = {
  SERVER:"var(--accent)", COMPUTER:"var(--accent3)", WORKSTATION:"var(--accent3)",
  PRINTER:"var(--warning)", ROUTER:"var(--success)", SWITCH:"var(--success)",
  FIREWALL:"var(--danger)", ACCESS_POINT:"var(--accent3)", NETWORK:"var(--success)",
  OTHER:"var(--text-3)",
};
const TYPE_LABELS = {
  SERVER:"Serveur", COMPUTER:"Poste", WORKSTATION:"Poste",
  PRINTER:"Imprimante", ROUTER:"Routeur", SWITCH:"Switch",
  FIREWALL:"Pare-feu", ACCESS_POINT:"Borne Wi-Fi", NETWORK:"Réseau", OTHER:"Autre",
};
const PAGE_SIZE = 12;

/** Formate une date en texte relatif */
function formatRelative(iso) {
  if (!iso) return "inconnu";
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60)    return `il y a ${diff} sec`;
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

/** Extrait le tableau depuis une ApiResponse */
function extractList(res) {
  if (Array.isArray(res))       return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

// ─────────────────────────────────────────────
//  MODALE — Créer un asset
// ─────────────────────────────────────────────

function CreateAssetModal({ companies, onSave, onClose }) {
  const [form, setForm] = useState({
    hostname:  "", ipAddress: "", os: "",
    type:      "COMPUTER", status: "OFFLINE",
    companyId: companies[0]?.id || "",
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    setError("");
    if (!form.hostname.trim()) return setError("Le hostname est obligatoire.");
    if (!form.companyId)       return setError("L'entreprise est obligatoire.");

    setLoading(true);
    try {
      await adminCreateAsset({
        hostname:  form.hostname,
        ipAddress: form.ipAddress || null,
        os:        form.os        || null,
        type:      form.type,
        status:    form.status,
        company:   { id: form.companyId },
      });
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="Nouvel équipement" onClose={onClose}
      footer={<>
        <button className="btn-itm btn-itm-outline" onClick={onClose}>Annuler</button>
        <button className="btn-itm btn-itm-primary" onClick={handleSubmit} disabled={loading}>
          {loading ? "Création…" : "Créer"}
        </button>
      </>}
    >
      {error && (
        <div style={{ background:"var(--danger-dim)", border:"1px solid var(--danger)", borderRadius:8, padding:"8px 12px", color:"var(--danger)", fontSize:13, marginBottom:12 }}>
          <i className="bi bi-exclamation-triangle me-2"/>{error}
        </div>
      )}

      {/* Entreprise */}
      <div className="form-group">
        <label className="itm-label">Entreprise *</label>
        <select className="itm-input" value={form.companyId} onChange={e => set("companyId", e.target.value)}>
          <option value="">— Choisir —</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="itm-label">Hostname *</label>
          <input className="itm-input" placeholder="PC-ALICE-01"
            value={form.hostname} onChange={e => set("hostname", e.target.value)}/>
        </div>
        <div className="form-group">
          <label className="itm-label">Adresse IP</label>
          <input className="itm-input" placeholder="192.168.1.50"
            value={form.ipAddress} onChange={e => set("ipAddress", e.target.value)}/>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label className="itm-label">Type</label>
          <select className="itm-input" value={form.type} onChange={e => set("type", e.target.value)}>
            {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="itm-label">Statut</label>
          <select className="itm-input" value={form.status} onChange={e => set("status", e.target.value)}>
            <option value="ONLINE">En ligne</option>
            <option value="OFFLINE">Hors ligne</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label className="itm-label">Système d'exploitation</label>
        <input className="itm-input" placeholder="Windows 11 Pro"
          value={form.os} onChange={e => set("os", e.target.value)}/>
      </div>
    </Modal>
  );
}

// ─────────────────────────────────────────────
//  PAGE PRINCIPALE
// ─────────────────────────────────────────────

export default function AssetsPage() {
  const navigate = useNavigate();

  // ── Entreprise sélectionnée ───────────────────────────────────────────────
  const [company,   setCompany]   = useState(null);

  // ── Données ───────────────────────────────────────────────────────────────
  const [assets,    setAssets]    = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  // ── Filtres ───────────────────────────────────────────────────────────────
  const [filterType,   setFilterType]   = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [search,       setSearch]       = useState("");
  const [viewMode,     setViewMode]     = useState("grid");
  const [page,         setPage]         = useState(0);

  // ── Modale ────────────────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);

  // Charge les entreprises au montage
  useEffect(() => {
    getCompanies().then(res => setCompanies(res.data || [])).catch(() => {});
  }, []);

  // Recharge les assets quand l'entreprise change
  useEffect(() => { fetchAssets(); }, [company]);

  const fetchAssets = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminGetAssets({ companyId: company?.id || undefined });
      setAssets(extractList(res));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filtrage côté client
  const filtered = useMemo(() => assets.filter(a => {
    if (filterType   !== "ALL" && a.type   !== filterType)   return false;
    if (filterStatus !== "ALL" && a.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      return a.hostname?.toLowerCase().includes(q)
          || a.ipAddress?.includes(search)
          || a.model?.toLowerCase().includes(q);
    }
    return true;
  }), [assets, filterType, filterStatus, search]);

  const paginated    = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const onlineCount  = filtered.filter(a => a.status === "ONLINE").length;
  const offlineCount = filtered.filter(a => a.status === "OFFLINE").length;

  return (
    <>
      <Topbar title="Parc" subtitle="informatique"/>
      <div className="page-body">

        {/* ── En-tête ── */}
        <div className="page-header">
          <div>
            <div className="page-title">Parc <span>informatique</span></div>
            <div className="page-sub">
              <span style={{ color:"var(--success)" }}>{onlineCount} en ligne</span>
              {" · "}
              <span style={{ color: offlineCount > 0 ? "var(--danger)" : "var(--text-3)" }}>
                {offlineCount} hors ligne
              </span>
              {" · "}{filtered.length} équipements
              {company && <span style={{ marginLeft:8, color:"var(--accent)" }}>— {company.name}</span>}
            </div>
          </div>
          <div className="page-actions">
            {/* Vue grille / liste */}
            <div style={{ display:"flex", gap:4, background:"var(--navy-soft)", borderRadius:10, padding:4 }}>
              {[["grid","bi-grid-3x3-gap"],["list","bi-list-ul"]].map(([m, icon]) => (
                <button key={m} onClick={() => setViewMode(m)} style={{
                  padding:"6px 10px", borderRadius:8, border:"none", cursor:"pointer",
                  background: viewMode===m ? "var(--navy-mid)" : "transparent",
                  color:      viewMode===m ? "var(--text-1)"   : "var(--text-3)",
                  fontSize:15, transition:"all .15s",
                }}>
                  <i className={`bi ${icon}`}/>
                </button>
              ))}
            </div>
            {/*<button className="btn-itm btn-itm-primary" onClick={() => setShowCreate(true)}>
              <i className="bi bi-plus-lg"/> Ajouter
            </button>*/}
          </div>
        </div>

        {/* ── Filtres ── */}
        <div className="filter-bar" style={{ marginBottom:14 }}>

          {/* Sélecteur d'entreprise */}
          <CompanySelector
            selectedId={company?.id || null}
            onChange={setCompany}
            placeholder="Toutes les entreprises"
          />

          {/* Type */}
          <select className="itm-select" value={filterType}
            onChange={e => { setFilterType(e.target.value); setPage(0); }}>
            <option value="ALL">Tous les types</option>
            {Object.entries(TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>

          {/* Statut */}
          <select className="itm-select" value={filterStatus}
            onChange={e => { setFilterStatus(e.target.value); setPage(0); }}>
            <option value="ALL">Tous les statuts</option>
            <option value="ONLINE">En ligne</option>
            <option value="OFFLINE">Hors ligne</option>
          </select>

          {/* Recherche */}
          <div className="input-group-itm" style={{ width:200, marginLeft:"auto" }}>
            <i className="bi bi-search input-icon"/>
            <input className="itm-input" placeholder="Hostname, IP…" value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}/>
          </div>
        </div>

        {/* ── Chargement ── */}
        {loading && (
          <div style={{ textAlign:"center", padding:"60px 0", color:"var(--text-3)" }}>
            <i className="bi bi-arrow-repeat" style={{ fontSize:24, display:"block", marginBottom:8 }}/>
            Chargement des équipements…
          </div>
        )}

        {/* ── Erreur ── */}
        {error && !loading && (
          <div style={{ background:"var(--danger-dim)", border:"1px solid var(--danger)", borderRadius:10, padding:14, color:"var(--danger)", display:"flex", alignItems:"center", gap:12, marginBottom:14 }}>
            <i className="bi bi-exclamation-triangle"/>{error}
            <button className="btn-itm btn-itm-outline" style={{ marginLeft:"auto" }} onClick={fetchAssets}>
              Réessayer
            </button>
          </div>
        )}

        {/* ── Vide ── */}
        {!loading && !error && filtered.length === 0 && (
          <div style={{ textAlign:"center", padding:"60px 0", color:"var(--text-3)" }}>
            <i className="bi bi-inbox" style={{ fontSize:32, display:"block", marginBottom:10 }}/>
            Aucun équipement trouvé
          </div>
        )}

        {/* ── Vue grille ── */}
        {!loading && !error && viewMode === "grid" && filtered.length > 0 && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:14 }}>
              {paginated.map((a, i) => (
                <div key={a.id} className="itm-card anim-fade-up"
                  style={{ animationDelay:`${i*.04}s`, cursor:"pointer", transition:"transform .2s, box-shadow .2s", position:"relative" }}
                  onClick={() => navigate(`/assets/${a.id}`)}
                  onMouseEnter={e => { e.currentTarget.style.transform="translateY(-2px)"; e.currentTarget.style.boxShadow="0 8px 24px rgba(0,0,0,.3)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform="translateY(0)";    e.currentTarget.style.boxShadow=""; }}
                >
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:14 }}>
                    <div style={{ width:42, height:42, borderRadius:12, background:"var(--navy-soft)", border:"1px solid var(--border)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20, color: ICON_COLORS[a.type] || "var(--text-3)" }}>
                      <i className={`bi ${ICONS[a.type] || "bi-cpu"}`}/>
                    </div>
                    <AssetStatusBadge status={a.status}/>
                  </div>

                  <div style={{ fontWeight:600, fontSize:14, color:"var(--text-1)", marginBottom:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                    {a.hostname || "—"}
                  </div>
                  <div style={{ fontSize:12, color:"var(--text-3)", marginBottom:4, fontFamily:"monospace" }}>
                    {a.ipAddress || "—"}
                  </div>
                  {/* Entreprise */}
                  {a.company && (
                    <div style={{ fontSize:11, color:"var(--accent)", marginBottom:4 }}>
                      <i className="bi bi-building me-1"/>{a.company.name}
                    </div>
                  )}
                  <div style={{ fontSize:12, color:"var(--text-2)", marginBottom:4 }}>{a.os || "—"}</div>
                  {a.ramGb && <div style={{ fontSize:12, color:"var(--text-3)" }}>{a.ramGb} Go RAM</div>}
                  <div style={{ fontSize:11, color: a.status === "OFFLINE" ? "var(--danger)" : "var(--text-3)", marginTop:8 }}>
                    <i className="bi bi-clock me-1"/>Dernier contact : {formatRelative(a.lastSeenAt)}
                  </div>
                </div>
              ))}
            </div>
            {filtered.length > PAGE_SIZE && (
              <div style={{ marginTop:16 }}>
                <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage}/>
              </div>
            )}
          </>
        )}

        {/* ── Vue liste ── */}
        {!loading && !error && viewMode === "list" && filtered.length > 0 && (
          <div className="itm-card anim-fade-up">
            <table className="itm-table">
              <thead>
                <tr><th>Équipement</th><th>Entreprise</th><th>IP</th><th>OS</th><th>RAM</th><th>Statut</th><th>Dernier contact</th></tr>
              </thead>
              <tbody>
                {paginated.map(a => (
                  <tr key={a.id} style={{ cursor:"pointer" }} onClick={() => navigate(`/assets/${a.id}`)}>
                    <td>
                      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:32, height:32, borderRadius:8, background:"var(--navy-soft)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, color: ICON_COLORS[a.type] || "var(--text-3)", flexShrink:0 }}>
                          <i className={`bi ${ICONS[a.type] || "bi-cpu"}`}/>
                        </div>
                        <div>
                          <div className="cell-primary">{a.hostname || "—"}</div>
                          <div className="cell-sub">{TYPE_LABELS[a.type] || a.type}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ fontSize:12, color:"var(--accent)" }}>
                      {a.company?.name || "—"}
                    </td>
                    <td style={{ fontFamily:"monospace", fontSize:12, color:"var(--text-3)" }}>{a.ipAddress || "—"}</td>
                    <td style={{ fontSize:12, color:"var(--text-2)" }}>{a.os || "—"}</td>
                    <td style={{ fontSize:12, color:"var(--text-3)" }}>{a.ramGb ? `${a.ramGb} Go` : "—"}</td>
                    <td><AssetStatusBadge status={a.status}/></td>
                    <td style={{ fontSize:12, color: a.status === "OFFLINE" ? "var(--danger)" : "var(--text-3)" }}>
                      {formatRelative(a.lastSeenAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={page} total={filtered.length} pageSize={PAGE_SIZE} onChange={setPage}/>
          </div>
        )}

        {/* ── Modale création ── */}
        {showCreate && (
          <CreateAssetModal
            companies={companies}
            onSave={() => { setShowCreate(false); fetchAssets(); }}
            onClose={() => setShowCreate(false)}
          />
        )}
      </div>
    </>
  );
}